/**
 * cashBook.ts — Pure computation of the cash book / cash-flow report: money in
 * versus money out, day by day. This is also the report that makes a Day Close
 * auditable after the fact.
 *
 * Money movements come from three sources, and the one rule this file exists to
 * enforce is the sign of a customer payment:
 *
 *  1. **A refund is money OUT, not a negative receipt.** `useCreateCreditNote`
 *     records a refund as a *positive* `payments` row against the CREDIT_NOTE
 *     invoice. Treated naively it would inflate receipts. The direction comes
 *     from the parent invoice's type: a payment on a SALE is a receipt (in), a
 *     payment on a CREDIT_NOTE is a refund (out).
 *  2. Supplier payments and expenses are always money out.
 *
 * The running `cumulative` is relative to the start of the period (it opens at
 * zero), not an absolute drawer balance — the app has no reliable period-open
 * cash figure, so a relative cash-flow curve is the honest thing to show.
 *
 * Pure — no DB access. See `cashBookDataSource.ts` for the WatermelonDB bridge.
 */

export const CREDIT_NOTE = 'CREDIT_NOTE';

// ─── Inputs ──────────────────────────────────────────────────────────────────

export interface ReceiptInput {
  date: Date;
  amount: number;
  method: string;
  /** Parent invoice type — a payment on a CREDIT_NOTE is a refund (outflow). */
  invoiceType: string;
}

export interface OutflowInput {
  date: Date;
  amount: number;
  method: string;
}

export interface CashBookInput {
  receipts: ReceiptInput[];
  supplierPayments: OutflowInput[];
  expenses: OutflowInput[];
}

// ─── Output ──────────────────────────────────────────────────────────────────

export interface CashDay {
  /** Local calendar day, YYYY-MM-DD. */
  date: string;
  inflow: number;
  outflow: number;
  net: number;
  /** Running sum of net from the start of the period (opens at 0). */
  cumulative: number;
}

export interface MethodTotal {
  method: string;
  amount: number;
}

export interface CashBook {
  /** Days with any activity, ascending. */
  days: CashDay[];
  totalIn: number;
  totalOut: number;
  net: number;
  /** Receipts (money in) split by payment method, largest first. */
  receiptsByMethod: MethodTotal[];
  /** Outflow split by source. */
  refundTotal: number;
  supplierPaymentTotal: number;
  expenseTotal: number;
  receiptTotal: number;
}

function round2(n: number): number {
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

/** Local calendar-day key, YYYY-MM-DD — buckets movements by the shop's day. */
function dayKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

const KNOWN_METHODS = ['CASH', 'UPI', 'CARD'];

export function buildCashBook(input: CashBookInput): CashBook {
  const { receipts, supplierPayments, expenses } = input;

  // Per-day accumulation of inflow/outflow.
  const dayMap = new Map<string, { inflow: number; outflow: number }>();
  const bump = (date: Date, field: 'inflow' | 'outflow', amount: number) => {
    const key = dayKey(date);
    const acc = dayMap.get(key) ?? { inflow: 0, outflow: 0 };
    acc[field] += amount;
    dayMap.set(key, acc);
  };

  let receiptTotal = 0;
  let refundTotal = 0;
  const receiptMethod = new Map<string, number>();

  for (const r of receipts) {
    if (r.invoiceType === CREDIT_NOTE) {
      refundTotal += r.amount;
      bump(r.date, 'outflow', r.amount);
    } else {
      receiptTotal += r.amount;
      bump(r.date, 'inflow', r.amount);
      const method = KNOWN_METHODS.includes(r.method) ? r.method : 'OTHER';
      receiptMethod.set(method, (receiptMethod.get(method) ?? 0) + r.amount);
    }
  }

  let supplierPaymentTotal = 0;
  for (const p of supplierPayments) {
    supplierPaymentTotal += p.amount;
    bump(p.date, 'outflow', p.amount);
  }

  let expenseTotal = 0;
  for (const e of expenses) {
    expenseTotal += e.amount;
    bump(e.date, 'outflow', e.amount);
  }

  // Days, ascending, with a running cumulative net.
  let cumulative = 0;
  const days: CashDay[] = [...dayMap.entries()]
    .sort((a, b) => (a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0))
    .map(([date, { inflow, outflow }]) => {
      const net = inflow - outflow;
      cumulative += net;
      return {
        date,
        inflow: round2(inflow),
        outflow: round2(outflow),
        net: round2(net),
        cumulative: round2(cumulative),
      };
    });

  const receiptsByMethod = [...receiptMethod.entries()]
    .map(([method, amount]) => ({ method, amount: round2(amount) }))
    .sort((a, b) => b.amount - a.amount);

  const totalIn = receiptTotal;
  const totalOut = refundTotal + supplierPaymentTotal + expenseTotal;

  return {
    days,
    totalIn: round2(totalIn),
    totalOut: round2(totalOut),
    net: round2(totalIn - totalOut),
    receiptsByMethod,
    refundTotal: round2(refundTotal),
    supplierPaymentTotal: round2(supplierPaymentTotal),
    expenseTotal: round2(expenseTotal),
    receiptTotal: round2(receiptTotal),
  };
}
